import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AdmissionStateService } from '@core/services/admission-state';

type AdmissionRouteStep =
  | 'student-form'
  | 'schooltransport'
  | 'own-transport'
  | 'medical'
  | 'age'
  | 'noc'
  | 'menu'
  | 'fee';

type AdmissionData = {
  TransportMode?: string | null;
  Student?: {
    Grade?: string | number | null;
    Class?: string | number | null;
    ClassName?: string | number | null;
    DateOfBirth?: string | Date | null;
    Dob?: string | Date | null;
  } | null;
  Declarations?: {
    AdmissionDeclarationAccepted?: boolean;
    TransportDeclarationAccepted?: boolean;
    MedicalUndertakingAccepted?: boolean;
    AgeDeclarationAccepted?: boolean;
    NoObjectionDeclarationAccepted?: boolean;
  } | null;
};

function hasStudentId(): boolean {
  return !!sessionStorage.getItem('studentId');
}

function normalizeReqFrom(value: string | null): string {
  return String(value ?? '').trim().toLowerCase();
}

function isAdminPreviewRequest(): boolean {
  return normalizeReqFrom(sessionStorage.getItem('ReqFrom')) === 'admin';
}

function isPaymentLinkRequest(): boolean {
  return normalizeReqFrom(
    sessionStorage.getItem('PaymentAccessReqFrom') || sessionStorage.getItem('ReqFrom'),
  ) === 'link';
}

function buildPaymentUrl(router: Router): UrlTree {
  const reportParams = sessionStorage.getItem('paymentLinkReportParams');
  const studentId =
    sessionStorage.getItem('paymentLinkStudentId') || sessionStorage.getItem('studentId');

  if (reportParams) {
    return router.parseUrl(`/admission/paymentlink/${encodeURIComponent(reportParams)}`);
  }

  return studentId
    ? router.parseUrl(`/admission/paymentlink?studentId=${encodeURIComponent(studentId)}`)
    : router.parseUrl('/admission/paymentlink');
}

function tryHydrateFromReportParams(encoded: string | null): void {
  if (!encoded) {
    return;
  }

  sessionStorage.setItem('paymentLinkReportParams', encoded);

  try {
    const decoded = atob(decodeURIComponent(encoded));
    const parsed = JSON.parse(decoded) as {
      studentId?: string | number;
      StudentId?: string | number;
      id?: string | number;
      Id?: string | number;
      ReqFrom?: string;
      reqFrom?: string;
    };

    const rawStudentId =
      parsed?.studentId ?? parsed?.StudentId ?? parsed?.id ?? parsed?.Id ?? null;
    const studentIdMatch = String(rawStudentId ?? '').match(/\d+/);
    const studentId = studentIdMatch ? Number(studentIdMatch[0]) : NaN;
    const reqFrom = String(parsed?.ReqFrom ?? parsed?.reqFrom ?? '').trim();

    if (studentId > 0) {
      sessionStorage.setItem('studentId', String(studentId));
      sessionStorage.setItem('paymentLinkStudentId', String(studentId));
    }

    if (reqFrom) {
      sessionStorage.setItem('PaymentAccessReqFrom', reqFrom);
      sessionStorage.setItem('ReqFrom', reqFrom);
    }
  } catch {
    // ignore malformed reportparams and continue with existing flow
  }
}

function getTransportMode(data: AdmissionData | null): string | null {
  const mode = data?.TransportMode;
  return typeof mode === 'string' && mode.trim() ? mode.trim() : null;
}

function isStudentStepComplete(data: AdmissionData | null): boolean {
  return hasStudentId() && data?.Declarations?.AdmissionDeclarationAccepted === true;
}

function isTransportStepComplete(data: AdmissionData | null): boolean {
  return data?.Declarations?.TransportDeclarationAccepted === true;
}

function isMedicalStepComplete(data: AdmissionData | null): boolean {
  return data?.Declarations?.MedicalUndertakingAccepted === true;
}

function normalizeGradeLabel(raw: unknown): string {
  return String(raw ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function extractGradeLevel(rawGrade: unknown): number | null {
  const normalized = normalizeGradeLabel(rawGrade);
  if (!normalized) return null;

  const prePrimaryAliases = [
    'nursery',
    'prenursery',
    'prekg',
    'playgroup',
    'pp1',
    'pp2',
    'lkg',
    'ukg',
    'kg',
    'kindergarten',
  ];

  if (prePrimaryAliases.some((alias) => normalized.includes(alias))) {
    return 0;
  }

  const match = normalized.match(/(?:grade|class|std|standard)?(\d{1,2})/);
  if (!match) return null;

  return Number(match[1]);
}

function parseDob(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();
  const ddMmYyyy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (ddMmYyyy) {
    const [, dd, mm, yyyy] = ddMmYyyy;
    const parsed = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(raw);
  return isNaN(parsed.getTime()) ? null : parsed;
}

function calculateAgeYears(dob: Date): number {
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

function isAgeUndertakingRequired(data: AdmissionData | null): boolean {
  const gradeRaw =
    data?.Student?.Grade ??
    data?.Student?.Class ??
    data?.Student?.ClassName ??
    null;
  const gradeLevel = extractGradeLevel(gradeRaw);

  // Only skip age undertaking when we can confidently confirm Grade 1 age eligibility.
  if (gradeLevel !== 1) {
    return true;
  }

  const dobValue = data?.Student?.DateOfBirth ?? data?.Student?.Dob ?? null;
  const dob = parseDob(dobValue);
  if (!dob) {
    return true;
  }

  return calculateAgeYears(dob) < 6;
}

function isAgeStepComplete(data: AdmissionData | null): boolean {
  if (!isAgeUndertakingRequired(data)) {
    return true;
  }
  return data?.Declarations?.AgeDeclarationAccepted === true;
}

function isNocStepComplete(data: AdmissionData | null): boolean {
  return typeof data?.Declarations?.NoObjectionDeclarationAccepted === 'boolean';
}

function isMenuStepComplete(): boolean {
  return sessionStorage.getItem('menuStepCompleted') === 'true';
}

function getExpectedTransportRoute(data: AdmissionData | null): string {
  return getTransportMode(data) === 'SchoolTransport'
    ? '/admission/schooltransport'
    : '/admission/own-transport';
}

function getFirstAllowedRoute(data: AdmissionData | null): string {
  if (!isStudentStepComplete(data)) {
    return '/admission/student-form';
  }

  if (!isTransportStepComplete(data)) {
    return getExpectedTransportRoute(data);
  }

  if (!isMedicalStepComplete(data)) {
    return '/admission/medical';
  }

  if (!isNocStepComplete(data)) {
    return '/admission/noc';
  }

  if (!isAgeStepComplete(data)) {
    return '/admission/age';
  }

  if (!isMenuStepComplete()) {
    return '/admission/menu';
  }

  return '/admission/fee';
}

function resolveStepAccess(step: AdmissionRouteStep, data: AdmissionData | null): true | string {
  if (step === 'student-form') {
    return true;
  }

  if (!isStudentStepComplete(data)) {
    return '/admission/student-form';
  }

  if (step === 'schooltransport' || step === 'own-transport') {
    const expectedTransportRoute = getExpectedTransportRoute(data);
    const requestedTransportRoute =
      step === 'schooltransport' ? '/admission/schooltransport' : '/admission/own-transport';

    return expectedTransportRoute === requestedTransportRoute
      ? true
      : expectedTransportRoute;
  }

  if (!isTransportStepComplete(data)) {
    return getExpectedTransportRoute(data);
  }

  if (step === 'medical') {
    return true;
  }

  if (!isMedicalStepComplete(data)) {
    return '/admission/medical';
  }

  if (step === 'noc') {
    return true;
  }

  if (!isNocStepComplete(data)) {
    return '/admission/noc';
  }

  if (step === 'age') {
    return isAgeUndertakingRequired(data) ? true : '/admission/menu';
  }

  if (!isAgeStepComplete(data)) {
    return '/admission/age';
  }

  if (step === 'menu') {
    return true;
  }

  if (!isMenuStepComplete()) {
    return '/admission/menu';
  }

  if (step === 'fee') {
    return true;
  }

  return getFirstAllowedRoute(data);
}

export const admissionStepGuard: CanActivateFn = (route): boolean | UrlTree | Observable<boolean | UrlTree> => {
  const router = inject(Router);
  const admissionState = inject(AdmissionStateService);
  const step = route.data?.['admissionStep'] as AdmissionRouteStep | undefined;
  const reportParams = route.queryParamMap.get('reportparams');

  if (!step) {
    return true;
  }

  // External preview deep-link support: hydrate session from reportparams when present.
  tryHydrateFromReportParams(reportParams);

  const adminPreview = isAdminPreviewRequest();
  const paymentLinkRequest = isPaymentLinkRequest();

  if (paymentLinkRequest) {
    return buildPaymentUrl(router);
  }

  if (adminPreview && step === 'student-form') {
    // Only auto-forward admin preview users to menu when the request is coming
    // from an active reportparams deep-link. This prevents stale ReqFrom/studentId
    // from re-routing after session-timeout recovery.
    return reportParams && hasStudentId()
      ? router.parseUrl('/admission/menu')
      : true;
  }

  // Admin preview sessions are restricted to menu/preview pages only.
  if (adminPreview && step !== 'menu') {
    return router.parseUrl('/admission/menu');
  }

  if (step === 'student-form') {
    return true;
  }

  if (!hasStudentId()) {
    return router.parseUrl('/admission/student-form');
  }

  const id = Number(sessionStorage.getItem('studentId'));
  if (!id) {
    return router.parseUrl('/admission/student-form');
  }

  // For admin-origin preview sessions, prefetch admission before entering menu/preview pages.
  // This avoids transport-card flicker and ensures mode-specific preview cards render immediately.
  if (step === 'menu' && adminPreview) {
    return admissionState.fetchAdmissionById(id).pipe(map(() => true));
  }

  return admissionState.fetchAdmissionById(id).pipe(
    map((admissionData) => {
      const access = resolveStepAccess(step, admissionData);
      return access === true ? true : router.parseUrl(access);
    }),
  );
};
